import React from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useGhgOrganization, useLanguage } from 'src/common/hooks';
import PageLoading from 'src/components/PageLoading';
import DeclineReasonForm from 'src/Ghg/components/DeclineReasonForm';
import {
  useCommitmentTarget,
  useCommitmentTargetPreview,
  useDeclineCommitmentTarget,
  useRabenConsent,
} from 'src/Ghg/hooks';
import {
  CommitmentTargetDeclineSchema,
  DeclinePayload,
} from 'src/Ghg/interfaces';
import { downloadCommitmentTargetPdf } from './TargetPdfDocument';
import {
  formatStatementDecimal,
  resolveStatementLanguage,
} from './statementFormat';
import TargetStatementText, {
  StatementReductionSentence,
} from './TargetStatementText';
import { ActionsRow, ConfirmationBox } from './index.styles';

const T_PREFIX = 'ghg.supplierCommitment.target';
const TAB_PREFIX = 'ghg.supplierCommitment';
const DECLINE_PREFIX = 'ghg.declineReason';
const OTHER_REASON = 'other';

// pdfStamp.ts refuses to stamp a value that does not fit its blank (a long
// company name in the 3 lines of "Podpis" at 7 pt) instead of drawing a
// clipped one into a document the carrier signs. That is the one failure the
// carrier can understand and act on, so it gets a message of its own; any
// other failure (network, a missing template) is not swallowed.
const isPdfValueTooLongError = (error: unknown): boolean =>
  /does not fit its blank/.test(
    String((error as { message?: unknown } | null)?.message ?? '')
  );

// The server answers 412 "stale_preview" when the emissions changed between
// the preview and the click. It is not in the global toast list, so the
// section shows its own warning for it.
function isStalePreviewError(error: unknown): boolean {
  return (
    (error as { response?: { status?: number } })?.response?.status === 412
  );
}

// RG 28.08 9.1/8.4/10.x (D2, D4, D5, D6). The target half of the
// "Program Zaangażowania Dostawców" tab, mounted by TargetArea (which owns
// the section heading, the "what is missing" note and the checklist).
//
// With no target yet it shows the full Target Statement with the base year
// as a dropdown right in the text; picking a year loads the server's preview
// of the figures, and the carrier then rejects ("Odrzucam", with an optional
// reason) or accepts ("Akceptuję") exactly what is on screen. Accepting is
// what creates the target -- nothing is downloaded automatically (D6).
// "Change base year" runs the same path with "Cancel" instead of "Reject".
//
// BR-1 still holds: there is no client-side computation of target_year /
// reduction_pct / emissions anywhere in here -- the figures are the server's
// preview (or the stored row) and are only formatted for display.
//
// Only the carrier's own users (`is_direct_member === true`) get decision
// controls (D2). Everyone else sees the same text and values read-only; the
// backend would answer 403 -- and any 403 redirects to /403 -- so those
// buttons are never rendered for them.
export default function TargetSection() {
  const { t } = useTranslation(undefined, { keyPrefix: T_PREFIX });
  const { t: tDecline } = useTranslation(undefined, {
    keyPrefix: DECLINE_PREFIX,
  });
  const { t: tTab } = useTranslation(undefined, { keyPrefix: TAB_PREFIX });
  const organization = useGhgOrganization();
  const { language } = useLanguage();
  const consent = useRabenConsent(organization.id);
  const { data, isLoading, setTarget } = useCommitmentTarget(organization.id);
  const declineTarget = useDeclineCommitmentTarget(organization.id);

  const [selectedYear, setSelectedYear] = React.useState<number | ''>('');
  const [editing, setEditing] = React.useState(false);
  const [declining, setDeclining] = React.useState(false);
  const [stale, setStale] = React.useState(false);
  const [downloading, setDownloading] = React.useState(false);

  const preview = useCommitmentTargetPreview(
    organization.id,
    selectedYear === '' ? undefined : selectedYear
  );

  // A new (or replaced) target from the server ends whatever the user was
  // doing: the flow state is reset so the view moves on to "target set".
  const targetStamp = data?.target?.created_at;
  React.useEffect(() => {
    setEditing(false);
    setDeclining(false);
    setStale(false);
    setSelectedYear('');
  }, [targetStamp]);

  if (isLoading || !data || consent.isLoading) {
    return <PageLoading />;
  }

  const isMember = consent.data?.is_direct_member === true;
  const target = data.target ?? null;
  const eligibleYears = data.eligible_years ?? [];
  const declined: CommitmentTargetDeclineSchema | null = target
    ? null
    : (data.declined ?? null);
  const statementLang = resolveStatementLanguage(language);

  const startEditing = () => {
    setTarget.reset();
    setEditing(true);
    setDeclining(false);
    setStale(false);
    setSelectedYear('');
  };

  const stopEditing = () => {
    setEditing(false);
    setStale(false);
    setSelectedYear('');
  };

  const handleYearChange = (event: SelectChangeEvent<number | ''>) => {
    setSelectedYear(event.target.value as number | '');
    setStale(false);
  };

  const handleAccept = async () => {
    if (selectedYear === '' || !preview.data) return;
    try {
      // What the user saw is what gets accepted: the server compares the
      // base emissions with a fresh calculation and refuses if they moved.
      await setTarget.mutateAsync({
        baseYear: selectedYear,
        expectedBaseEmissions: preview.data.base_emissions,
      });
    } catch (error) {
      if (isStalePreviewError(error)) {
        setStale(true);
        preview.refetch();
      }
      // Any other failure was already reported by the global interceptor.
    }
  };

  const handleDecline = async (payload: DeclinePayload) => {
    try {
      await declineTarget.mutateAsync(payload);
      setDeclining(false);
    } catch {
      // Reported by the global interceptor; the form stays open.
    }
  };

  const handleDownload = async () => {
    if (!target) return;
    setDownloading(true);
    try {
      await downloadCommitmentTargetPdf({ target, language });
    } catch (error) {
      if (!isPdfValueTooLongError(error)) throw error;
      toast.error(tTab('pdfTooLong') as string);
    } finally {
      setDownloading(false);
    }
  };

  const declineReasonLine = (decline: CommitmentTargetDeclineSchema) => {
    if (!decline.reason) return null;
    const label = tDecline(`codes.${decline.reason}`, {
      defaultValue: decline.reason,
    });
    const detail =
      decline.reason === OTHER_REASON && decline.reason_text
        ? ` – ${decline.reason_text}`
        : '';
    return `${tDecline('reasonPrefix')} ${label}${detail}`;
  };

  const renderDeclinedNote = (decline: CommitmentTargetDeclineSchema) => {
    const reasonLine = declineReasonLine(decline);
    return (
      <Alert severity="info" role="status" sx={{ mb: 2 }}>
        {/* D2: "You have declined..." speaks to the carrier's own user; anyone
            else (the Raben admin) reads the same fact about the carrier. */}
        {t(isMember ? 'declinedStatement' : 'declinedStatementReadOnly')}
        {reasonLine && (
          <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
            {reasonLine}
          </Box>
        )}
      </Alert>
    );
  };

  // Target set and nothing being changed (10.1-10.3).
  if (target && !(editing && eligibleYears.length > 0)) {
    return (
      <Box sx={{ mt: 2 }}>
        <ConfirmationBox>
          {/* RG 28.08 10.1: the confirmation has to stand out. A success
              Alert with a much larger, bolder text than the data lines
              under it (body2). role="status" because it is a notice, not an
              error. */}
          <Alert
            severity="success"
            role="status"
            sx={{
              mb: 2,
              alignItems: 'center',
              '& .MuiAlert-message': { fontSize: 20, fontWeight: 600 },
            }}
          >
            {t('setStatement')}
          </Alert>
          {/* RG 28.08 10.2: the SBTi sentence, in the document's language. */}
          <StatementReductionSentence values={target} />
          <Typography variant="body2">
            {t('baseYearLabel')}: {target.base_year}
          </Typography>
          <Typography variant="body2">
            {t('targetYearLabel')}: {target.target_year}
          </Typography>
          <Typography variant="body2">
            {t('reductionPctLabel')}:{' '}
            {formatStatementDecimal(target.reduction_pct, statementLang)}%
          </Typography>
          <Typography variant="body2">
            {t('baseEmissionsLabel')}:{' '}
            {formatStatementDecimal(target.base_emissions, statementLang)} tCO2e
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('targetEmissionsLabel')}:{' '}
            {formatStatementDecimal(target.target_emissions, statementLang)}{' '}
            tCO2e
          </Typography>
          <ActionsRow>
            <Button
              variant="contained"
              onClick={handleDownload}
              disabled={downloading}
            >
              {t('downloadButton')}
            </Button>
            {isMember && (
              <Button
                variant="text"
                onClick={startEditing}
                disabled={eligibleYears.length === 0}
              >
                {t('changeBaseYear')}
              </Button>
            )}
          </ActionsRow>
        </ConfirmationBox>
      </Box>
    );
  }

  // No target and nothing to pick from: the only thing worth showing is a
  // recorded refusal (TargetArea explains the missing years).
  if (eligibleYears.length === 0) {
    return declined ? (
      <Box sx={{ mt: 2 }}>{renderDeclinedNote(declined)}</Box>
    ) : null;
  }

  const yearSelect = (
    <FormControl
      size="small"
      sx={{ minWidth: 130, mx: 0.5, verticalAlign: 'middle' }}
    >
      <Select<number | ''>
        value={selectedYear}
        displayEmpty
        onChange={handleYearChange}
        renderValue={(value) => (value === '' ? t('selectYear') : value)}
        inputProps={{ 'aria-label': t('baseYearLabel') }}
        sx={{ fontWeight: 700 }}
      >
        {eligibleYears.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const canAccept =
    !!preview.data &&
    !preview.isFetching &&
    !setTarget.isLoading &&
    !setTarget.isSuccess;
  // "Reject" is only for a first decision; once refused (or while replacing
  // an existing target) it would be pointless -- the server refuses it too.
  const canDecline = !declined && !editing;

  return (
    <Box sx={{ mt: 2 }}>
      {declined && renderDeclinedNote(declined)}
      {editing && (
        <Alert severity="info" role="status" sx={{ mb: 2 }}>
          {t('replaceWarning')}
        </Alert>
      )}
      {stale && (
        <Alert severity="warning" role="status" sx={{ mb: 2 }}>
          {t('staleWarning')}
        </Alert>
      )}

      <ConfirmationBox>
        <TargetStatementText
          values={preview.data}
          yearSlot={isMember ? yearSelect : undefined}
        />

        {isMember && declining && (
          <DeclineReasonForm
            context="target"
            options={data.decline_reason_options}
            loading={declineTarget.isLoading}
            onConfirm={handleDecline}
            onCancel={() => setDeclining(false)}
          />
        )}

        {isMember && !declining && (
          <ActionsRow>
            {editing && (
              <Button variant="text" onClick={stopEditing}>
                {t('cancel')}
              </Button>
            )}
            {canDecline && (
              <Button variant="text" onClick={() => setDeclining(true)}>
                {t('reject')}
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleAccept}
              disabled={!canAccept}
            >
              {t('accept')}
            </Button>
          </ActionsRow>
        )}
      </ConfirmationBox>
    </Box>
  );
}
